import { useState } from "react";
import DeleteModal from "./DeleteModal";
import ApproveModal from "./ApproveModal";
import {
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
} from "@heroicons/react/24/solid";

const BlogTable = ({
  submittedBlogs,
  handleEdit,
  handleDelete,
  handleApprove,
  onAddNew,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [selectedBlogIndex, setSelectedBlogIndex] = useState(null);

  const handleDeleteConfirm = () => {
    handleDelete(selectedBlogIndex);
    setShowDeleteModal(false);
  };

  const handleApproveConfirm = () => {
    handleApprove(selectedBlogIndex);
    setShowApproveModal(false);
  };

  return (
    <div className="overflow-x-auto font-poppins">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-primary mb-4">Blogs Information</h2>
        <button
          onClick={() => onAddNew()}
          className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-600 mb-4"
        >
          <a href="#add-new-blog">Add New Blog</a>
        </button>
      </div>

      {submittedBlogs.length === 0 ? (
        <div className="text-center text-lg text-gray-500 py-4">
          No blogs available. Please add a new blog.
        </div>
      ) : (
        <table className="min-w-full table-auto bg-white shadow-md rounded-lg border-collapse mt-4">
          <thead>
            <tr className="bg-gray-100">
              <th className="py-2 px-4 border-b text-left align-middle">Blog Type</th>
              <th className="py-2 px-4 border-b text-left align-middle">Headline</th>
              <th className="py-2 px-4 border-b text-left align-middle">Author</th>
              <th className="py-2 px-4 border-b text-center align-middle">Actions</th>
            </tr>
          </thead>
          <tbody>
            {submittedBlogs.map((blog, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="py-2 px-4 border-b text-left align-middle">
                  {blog.blog_type?.name || "N/A"}
                </td>
                <td className="py-2 px-4 border-b text-left align-middle">{blog.title}</td>
                <td className="py-2 px-4 border-b text-left align-middle">{blog.author}</td>
                <td className="py-2 px-4 border-b align-middle text-center flex space-x-2 justify-center">
                  <button
                    onClick={() => {
                      setSelectedBlog(blog);
                      setShowDrawer(true);
                    }}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    <EyeIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleEdit(index)}
                    className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                  >
                    <PencilSquareIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedBlogIndex(index);
                      setShowDeleteModal(true);
                    }}
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <DeleteModal
        showModal={showDeleteModal}
        onDelete={handleDeleteConfirm}
        onCancel={() => setShowDeleteModal(false)}
      />

      <ApproveModal
        showModal={showApproveModal}
        onApprove={handleApproveConfirm}
        onCancel={() => setShowApproveModal(false)}
      />

      {showDrawer && (
        <div className="fixed top-0 right-0 h-screen inset-0 flex items-end justify-end z-50 overflow-y-scroll">
          <div
            className="absolute inset-0 bg-black bg-opacity-25"
            onClick={() => setShowDrawer(false)}
          ></div>

          <div className="bg-white p-6 w-[600px] h-full overflow-y-auto transition-transform transform shadow-lg">
            <button
              className="text-gray-700 text-xl absolute top-4 right-4"
              onClick={() => setShowDrawer(false)}
            >
              &times;
            </button>

            <h2 className="text-2xl font-bold mb-4">Blog Details</h2>

            {selectedBlog && (
              <div>
                <div className="mb-4">
                  <table className="w-full table-auto">
                    <tbody>
                      <tr>
                        <td className="px-6 py-3 font-semibold w-1/3">Headline:</td>
                        <td className="px-6 py-3">{selectedBlog.title}</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-3 font-semibold">Subheadline:</td>
                        <td className="px-6 py-3">{selectedBlog.subtitle}</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-3 font-semibold">Author:</td>
                        <td className="px-6 py-3">{selectedBlog.author}</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-3 font-semibold">Date:</td>
                        <td className="px-6 py-3">{selectedBlog.published_at}</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-3 font-semibold">Body:</td>
                        <td className="px-6 py-3">
                          {selectedBlog.content || "No content is available"}
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <div className="mt-4">
                    <h3 className="font-semibold mb-2">Images</h3>
                    {selectedBlog.blog_images?.length > 0 ? (
                      <div className="flex gap-4 flex-wrap">
                        {selectedBlog.blog_images.map((img, index) => (
                          <img
                            key={index}
                            src={img.image_url || img.preview}
                            alt={`Image ${index + 1}`}
                            className="w-24 h-24 object-cover rounded-lg border"
                          />
                        ))}
                      </div>
                    ) : (
                      <span>No Images</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogTable;
